import React, { useState, useRef } from 'react';
import { X, Upload, FileCheck, AlertCircle, CheckCircle2, Loader2, Activity } from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:3000/api' : '/api');

export default function FitUploadModal({ isOpen, onClose, onUploadSuccess }) {
    const [dragActive, setDragActive] = useState(false);
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    if (!isOpen) return null;

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            validateAndSetFile(e.dataTransfer.files[0]);
        }
    };

    const handleChange = (e) => {
        e.preventDefault();
        if (e.target.files && e.target.files[0]) {
            validateAndSetFile(e.target.files[0]);
        }
    };

    const validateAndSetFile = (selectedFile) => {
        setError(null);
        setResult(null);
        if (!selectedFile.name.toLowerCase().endsWith('.fit')) {
            setError('Por favor selecciona un archivo con extensión .fit válido de Garmin, Wahoo, Zwift o Bryton.');
            return;
        }
        if (selectedFile.size > 35 * 1024 * 1024) {
            setError('El archivo supera el límite máximo de 35 MB.');
            return;
        }
        setFile(selectedFile);
    };

    const handleUpload = async () => {
        if (!file) return;

        setUploading(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);

            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/activities/upload-fit`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`
                },
                body: formData
            });

            const data = await res.json();
            if (!res.ok) {
                throw new Error(data.error || 'Error al procesar el archivo .FIT');
            }

            setResult(data.summary || data.activity);
            if (onUploadSuccess) {
                onUploadSuccess(data.activity);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setFile(null);
        setResult(null);
        setError(null);
        onClose();
    };

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(8px)'
        }}>
            <div style={{
                position: 'relative',
                width: '100%',
                maxWidth: '520px',
                background: '#0e1726',
                border: '1px solid var(--glass-border)',
                borderRadius: '16px',
                padding: '1.5rem',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
                overflow: 'hidden'
            }}>
                {/* Botón cerrar */}
                <button
                    type="button"
                    onClick={handleClose}
                    style={{
                        position: 'absolute',
                        top: '1rem',
                        right: '1rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: 'none',
                        borderRadius: '50%',
                        padding: '6px',
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <X size={18} />
                </button>

                {/* Cabecera */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1.5rem' }}>
                    <div style={{
                        padding: '10px',
                        borderRadius: '12px',
                        background: 'rgba(99, 102, 241, 0.15)',
                        border: '1px solid rgba(99, 102, 241, 0.3)',
                        color: '#818cf8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Upload size={22} />
                    </div>
                    <div>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                            Importar Archivo .FIT Nativo
                        </h3>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '2px 0 0 0' }}>
                            Garmin, Wahoo, Zwift, Bryton o Apple Watch
                        </p>
                    </div>
                </div>

                {/* Área de Dropzone o Resumen */}
                {!result ? (
                    <>
                        <div
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => inputRef.current?.click()}
                            style={{
                                border: `2px dashed ${dragActive ? '#6366f1' : (file ? '#10b981' : 'rgba(255, 255, 255, 0.15)')}`,
                                borderRadius: '12px',
                                padding: '2rem 1rem',
                                textAlign: 'center',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                background: dragActive ? 'rgba(99, 102, 241, 0.1)' : (file ? 'rgba(16, 185, 129, 0.05)' : 'rgba(255, 255, 255, 0.02)'),
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px'
                            }}
                        >
                            <input
                                ref={inputRef}
                                type="file"
                                accept=".fit"
                                onChange={handleChange}
                                style={{ display: 'none' }}
                            />

                            {file ? (
                                <>
                                    <div style={{ padding: '10px', borderRadius: '50%', background: 'rgba(16, 185, 129, 0.2)', color: '#10b981' }}>
                                        <FileCheck size={28} />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff', margin: 0 }}>{file.name}</p>
                                        <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontFamily: 'monospace', margin: '4px 0 0 0' }}>
                                            {(file.size / 1024).toFixed(1)} KB
                                        </p>
                                    </div>
                                    <span style={{ fontSize: '0.75rem', color: '#818cf8', textDecoration: 'underline' }}>Cambiar archivo</span>
                                </>
                            ) : (
                                <>
                                    <div style={{ padding: '10px', borderRadius: '50%', background: 'rgba(99, 102, 241, 0.15)', color: '#818cf8' }}>
                                        <Upload size={28} />
                                    </div>
                                    <div>
                                        <p style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                                            Arrastra aquí tu archivo .FIT o haz clic para explorar
                                        </p>
                                        <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', margin: '4px 0 0 0' }}>
                                            Soporta telemetría completa de potencia, cadencia, pulso y GPS
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        {error && (
                            <div style={{
                                marginTop: '1rem',
                                padding: '0.75rem',
                                borderRadius: '8px',
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#fca5a5',
                                fontSize: '0.78rem'
                            }}>
                                <AlertCircle size={16} />
                                <span>{error}</span>
                            </div>
                        )}

                        <div style={{ marginTop: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px' }}>
                            <button
                                type="button"
                                onClick={handleClose}
                                disabled={uploading}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    fontSize: '0.78rem',
                                    fontWeight: 500,
                                    color: 'var(--text-secondary)',
                                    background: 'transparent',
                                    border: '1px solid var(--glass-border)',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleUpload}
                                disabled={!file || uploading}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '8px 18px',
                                    borderRadius: '8px',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    color: '#fff',
                                    background: (!file || uploading) ? 'rgba(99, 102, 241, 0.4)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                                    border: 'none',
                                    cursor: (!file || uploading) ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                                }}
                            >
                                {uploading ? (
                                    <>
                                        <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                        <span>Procesando telemetría...</span>
                                    </>
                                ) : (
                                    <>
                                        <Activity size={14} />
                                        <span>Procesar e Importar</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </>
                ) : (
                    /* Pantalla de Éxito */
                    <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                        <div style={{
                            width: '52px',
                            height: '52px',
                            margin: '0 auto 1rem auto',
                            borderRadius: '50%',
                            background: 'rgba(16, 185, 129, 0.2)',
                            border: '1px solid rgba(16, 185, 129, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#10b981'
                        }}>
                            <CheckCircle2 size={30} />
                        </div>
                        <h4 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#fff', margin: 0 }}>
                            ¡Entrenamiento Procesado con Éxito!
                        </h4>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '4px 0 1.25rem 0' }}>
                            {result.name || 'Actividad .FIT importada'}
                        </p>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '1.25rem' }}>
                            <div style={{ background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '10px', borderRadius: '8px' }}>
                                <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontFamily: 'monospace', display: 'block' }}>Distancia</span>
                                <span style={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', color: '#fff' }}>
                                    {result.distanceKm || (result.distance ? (result.distance / 1000).toFixed(1) : '0')} km
                                </span>
                            </div>
                            <div style={{ background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '10px', borderRadius: '8px' }}>
                                <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontFamily: 'monospace', display: 'block' }}>Tiempo</span>
                                <span style={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', color: '#fff' }}>
                                    {result.movingTimeMin || Math.round((result.movingTime || 0) / 60)} min
                                </span>
                            </div>
                            <div style={{ background: 'rgba(0, 0, 0, 0.3)', border: '1px solid rgba(255, 255, 255, 0.05)', padding: '10px', borderRadius: '8px' }}>
                                <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)', textTransform: 'uppercase', fontFamily: 'monospace', display: 'block' }}>Estrés TSS</span>
                                <span style={{ fontSize: '1rem', fontWeight: 800, fontFamily: 'monospace', color: '#10b981' }}>
                                    {result.tss || 0}
                                </span>
                            </div>
                        </div>

                        <p style={{
                            fontSize: '0.72rem',
                            color: 'var(--text-secondary)',
                            marginBottom: '1.25rem',
                            background: 'rgba(16, 185, 129, 0.05)',
                            border: '1px solid rgba(16, 185, 129, 0.2)',
                            padding: '10px',
                            borderRadius: '8px'
                        }}>
                            El Sanity Engine ha saneado los sensores y el Doble Modelo PMC ha sido actualizado en PostgreSQL.
                        </p>

                        <button
                            type="button"
                            onClick={handleClose}
                            style={{
                                width: '100%',
                                padding: '10px',
                                borderRadius: '8px',
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                color: '#fff',
                                background: '#10b981',
                                border: 'none',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                            }}
                        >
                            Ver en el Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
